// The tab's title (INC-14a-3a-1): the section a screen is showing, the
// organization and, in the product's own experiences, the product.
//
// The office and the field read "[Sección] · [Organización] · [Producto]"; the
// end customer's portal and the screens before entering read
// "[Sección] · [Organización]", with no product (D-293 keeps the product's
// name out of what the end customer reads, and the sign in is anyone's). The
// section is what the screen's own header says, in the organization's words:
// each screen declares it where it renders that header (`useTitleSection`),
// and the one place that writes the document's title (App.tsx) composes it as
// the screens change. Only the title changes: nothing on the screen does.
// The product's word and the separator are the catalog's.
//
// INC-16 (D-421): under the organization's white label the product's word is
// empty (i18n/index.ts), and left out like anything empty: the office and the
// field read "[Sección] · [Organización]", and a screen with no organization's
// name reads its section alone.
import { useEffect, useSyncExternalStore } from 'react';

/** Whose experience a screen is: the product's own (office and field), whose
 *  title names the product, or the outside one (the portal and the screens
 *  before entering), whose title never does. */
export type TitleExperience = 'product' | 'outside';

export interface TitleSection {
  section: string;
  experience: TitleExperience;
}

export interface TitleWords {
  separator: string;
  product: string;
}

/** The title for a section of an organization: the section, the organization
 *  and, for the product's experiences alone, the product; what is empty is
 *  left out. Nothing at all is a zero-width space, so the tab names nobody
 *  until the organization is known (index.html). */
export function composeTitle(
  section: TitleSection | null,
  organization: string,
  words: TitleWords,
): string {
  const parts = [section?.section ?? '', organization];
  if (section?.experience === 'product') parts.push(words.product);
  const named = parts.map((part) => part.trim()).filter((part) => part !== '');
  return named.length === 0 ? '​' : named.join(words.separator);
}

/** The text with its first letter in capitals, as a header whose first letter
 *  the sheet raises shows it (the field's, ui/frame.css). */
export function firstLetterUp(text: string): string {
  return text.charAt(0).toLocaleUpperCase() + text.slice(1);
}

// The section on screen, one at a time: the last screen that declared one,
// until it leaves.
let current: (TitleSection & { owner: object }) | null = null;
const listeners = new Set<() => void>();

function publish(next: (TitleSection & { owner: object }) | null): void {
  current = next;
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function snapshot(): TitleSection | null {
  return current;
}

/** The section a screen shows, for the tab's title; null declares none. */
export function useTitleSection(section: string | null, experience: TitleExperience): void {
  useEffect(() => {
    if (section === null || section.trim() === '') return undefined;
    const owner = {};
    publish({ section, experience, owner });
    return () => {
      // A screen that leaves takes its section with it, unless another one
      // already declared its own.
      if (current?.owner === owner) publish(null);
    };
  }, [section, experience]);
}

/** The section on screen now, as the tab's title reads it. */
export function useTitleSectionOnScreen(): TitleSection | null {
  return useSyncExternalStore(subscribe, snapshot, snapshot);
}
